/**
 * OutcomeGraphView component displays a fullscreen graph visualization
 * for a specific outcome (product tree).
 * Uses the shared LayoutFlowStatic component.
 */
import { useEffect, useMemo } from "react";
import { ReactFlowProvider } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { LayoutFlowStatic } from "@unlikelyai-magic/shared/components";
import {
  DecisionTree,
  PaneType,
  ProductTree,
} from "@unlikelyai-magic/shared/data-access/api";

interface GraphViewProps {
  /**
   * The product tree to display
   */
  tree: ProductTree;
}

/**
 * OutcomeGraphView component that renders a fullscreen graph visualization
 * of a product tree using ReactFlow.
 * Uses the shared LayoutFlowStatic component.
 *
 * @param props - The component props
 * @param props.tree - The product tree to display
 * @returns A fullscreen graph view
 */
export const GraphView = ({ tree: productTree }: GraphViewProps) => {
  // Suppress ResizeObserver error from ReactFlow
  useEffect(() => {
    const handleError = (e: ErrorEvent) => {
      const errorMessage = e.message || e.error?.message || "";
      if (
        errorMessage.includes(
          "ResizeObserver loop completed with undelivered notifications"
        ) ||
        errorMessage.includes("ResizeObserver loop limit exceeded")
      ) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return false;
      }
    };
    // Also handle unhandled promise rejections that might contain ResizeObserver errors
    const handleUnhandledRejection = (e: PromiseRejectionEvent) => {
      const errorMessage = e.reason?.message || String(e.reason || "");
      if (
        errorMessage.includes(
          "ResizeObserver loop completed with undelivered notifications"
        ) ||
        errorMessage.includes("ResizeObserver loop limit exceeded")
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Use capture phase to catch the error earlier
    window.addEventListener("error", handleError, true);
    window.addEventListener(
      "unhandledrejection",
      handleUnhandledRejection,
      true
    );

    return () => {
      window.removeEventListener("error", handleError, true);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
        true
      );
    };
  }, []);

  // Prepare panes for LayoutFlowStatic
  const panes = useMemo(() => {
    return [
      {
        paneType: PaneType.DEFINITION,
        definition: {
          rootNode: productTree.template,
          name: productTree.name,
          description: productTree.description,
        } as DecisionTree,
        id: productTree.id,
        explanation: productTree.description,
        excerpt: productTree.description,
      },
    ];
  }, [productTree]);

  // Render LayoutFlowStatic directly
  const layoutFlowStatic = useMemo(() => {
    return <LayoutFlowStatic panes={panes} questionAnswers={[]} />;
  }, [panes]);

  return (
    <div className="h-full w-full">
      <ReactFlowProvider key={productTree.id}>
        {layoutFlowStatic}
      </ReactFlowProvider>
    </div>
  );
};
